import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { FeatureFlag, featureFlagService } from '../services/featureFlagService';

// FEAT-059 admin surface (MVP-18.37).
// Lists every persisted runtime feature flag and exposes a per-flag toggle so
// admins can enable/disable features at runtime — no deployment required. Each
// toggle PUTs to /feature-flags/:key and reflects the persisted state on success.

export const FeatureFlagsPage: React.FC = () => {
  const [flags, setFlags] = React.useState<FeatureFlag[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [savingKey, setSavingKey] = React.useState<string | null>(null);

  const loadFlags = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await featureFlagService.listFlags();
      setFlags(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadFlags();
  }, [loadFlags]);

  const handleToggle = async (flag: FeatureFlag) => {
    const nextEnabled = !flag.enabled;
    setSavingKey(flag.key);
    setError(null);
    setSuccess(null);
    try {
      const updated = await featureFlagService.setFlag(
        flag.key,
        nextEnabled,
        flag.description ?? undefined
      );
      setFlags((prev) =>
        prev.map((f) => (f.key === updated.key ? { ...f, ...updated } : f))
      );
      setSuccess(
        `${updated.key} is now ${updated.enabled ? 'enabled' : 'disabled'}.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to update ${flag.key}`);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} data-testid="feature-flags-page">
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Feature Flags
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enable or disable features at runtime. Changes take effect
            immediately for subsequent API reads — no deployment required.
          </Typography>
        </Box>

        {error ? (
          <Alert severity="error" onClose={() => setError(null)} data-testid="feature-flags-error">
            {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert
            severity="success"
            onClose={() => setSuccess(null)}
            data-testid="feature-flags-success"
          >
            {success}
          </Alert>
        ) : null}

        <Stack direction="row" spacing={2}>
          <Button
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={() => void loadFlags()}
            disabled={loading}
            data-testid="feature-flags-refresh"
          >
            Refresh
          </Button>
        </Stack>

        <Paper variant="outlined">
          {loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" aria-label="Feature flags table">
                <TableHead>
                  <TableRow>
                    <TableCell>Flag key</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="right">Enabled</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {flags.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 3, textAlign: 'center' }}
                          data-testid="feature-flags-empty"
                        >
                          No feature flags have been created yet. Flags appear
                          here once a feature writes one or an admin toggles it
                          via the API.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    flags.map((flag) => (
                      <TableRow key={flag.key} hover data-testid={`feature-flag-row-${flag.key}`}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {flag.key}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 420 }}>
                          {flag.description ? flag.description : '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={flag.enabled ? 'Enabled' : 'Disabled'}
                            color={flag.enabled ? 'success' : 'default'}
                            variant={flag.enabled ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip
                            title={flag.enabled ? 'Disable feature' : 'Enable feature'}
                          >
                            <span>
                              <Switch
                                checked={flag.enabled}
                                onChange={() => void handleToggle(flag)}
                                disabled={savingKey === flag.key}
                                inputProps={
                                  {
                                    'aria-label': `Toggle ${flag.key} feature flag`,
                                    'data-testid': `feature-flag-toggle-${flag.key}`,
                                  } as React.InputHTMLAttributes<HTMLInputElement>
                                }
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>
    </Container>
  );
};

export default FeatureFlagsPage;
